import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import React from 'react';

import { useSiteSettings } from '../contexts/SiteSettingsContext';
import { t } from '../i18n/translator';
import { queryKeys } from '../lib/queryClient';
import { tasksService, type TaskItem, type TaskProgressLine } from '../services/tasks.service';
import styles from '../styles/components/tasks-widget.module.scss';
import { formatPaymentAmount } from '../utils/amount';
import { logger } from '../utils/logger';

import { useToast } from './Toast';

function conditionLabel(type: string): string {
  if (type === 'deposit_count') return t(2603);
  if (type === 'deposit_amount') return t(2604);
  if (type === 'login_streak_days') return t(2605);
  if (type === 'login_days_in_window') return t(2606);
  if (type === 'game_round_count_in_window') return t(2607);
  return type;
}

function formatProgressLine(line: TaskProgressLine, currency: unknown): string {
  const label = conditionLabel(line.type);
  if (line.type === 'deposit_amount') {
    return `${label}: ${formatPaymentAmount(line.current, currency)} / ${formatPaymentAmount(line.target, currency)}`;
  }
  return `${label}: ${line.current} / ${line.target}`;
}

function progressPercent(line: TaskProgressLine): number {
  if (!line.target || line.target <= 0) {
    return 0;
  }
  const ratio = (line.current / line.target) * 100;
  return Math.max(0, Math.min(100, Math.round(ratio)));
}

function getTaskStatusLabel(task: TaskItem): string {
  if (task.status === 'claimed') {
    return t(2608);
  }
  if (task.status === 'completed') {
    return task.claimable ? t(2609) : t(2461);
  }
  return t(2459);
}

function getTaskButtonLabel(task: TaskItem, isClaiming: boolean): string {
  if (isClaiming) {
    return '...';
  }
  if (task.status === 'claimed') {
    return t(2608);
  }
  if (task.claimable) {
    return t(2609);
  }
  if (task.status === 'completed') {
    return t(2461);
  }
  return t(2610);
}

type ClaimFeedback = { type: 'success' | 'error'; message: string };

type Props = {
  userKey: string | number;
  onAfterClaim?: () => Promise<void> | void;
  /** Başlık metni (varsayılan: Görevler) */
  title?: string;
  /** true iken görev yokken boş mesaj yerine hiçbir şey gösterme */
  hideWhenEmpty?: boolean;
  appearance?: 'default' | 'wheel';
  /**
   * Claim sonucunu dışarıya verir. Verildiğinde mesajı çağrıldığı yerdeki
   * tek kanala yönlendirir; aksi durumda ortak bildirim sistemini kullanır.
   */
  onFeedback?: (feedback: ClaimFeedback | null) => void;
};

const TasksWidget: React.FC<Props> = ({
  userKey,
  onAfterClaim,
  title = t(2441),
  hideWhenEmpty = false,
  appearance = 'default',
  onFeedback,
}) => {
  const queryClient = useQueryClient();
  const { settings } = useSiteSettings();
  const { showToast } = useToast();
  const [claimingTaskKey, setClaimingTaskKey] = React.useState<string | null>(null);
  const [openTaskKey, setOpenTaskKey] = React.useState<string | null>(null);

  const tasksQuery = useQuery({
    queryKey: queryKeys.tasks.list(settings.site_id, userKey),
    queryFn: async () => {
      try {
        return await tasksService.list();
      } catch (error) {
        logger.warn('TasksWidget', 'Failed to load tasks', error);
        throw error;
      }
    },
    staleTime: 10_000,
    enabled: String(userKey) !== 'guest',
  });

  const publishFeedback = React.useCallback(
    (feedback: ClaimFeedback | null) => {
      if (onFeedback) {
        onFeedback(feedback);
      } else if (feedback) {
        showToast(feedback.type, feedback.message);
      }
    },
    [onFeedback, showToast]
  );

  const claimMutation = useMutation({
    mutationFn: async (taskKey: string) => {
      setClaimingTaskKey(taskKey);
      publishFeedback(null);
      return tasksService.claim(taskKey);
    },
    onSuccess: async (result) => {
      await queryClient.invalidateQueries({
        queryKey: queryKeys.tasks.list(settings.site_id, userKey),
      });
      await onAfterClaim?.();

      const creditsAdded = result.credits_added;
      const availableCredits = result.available_credits;
      const responseMessage =
        typeof result.message === 'string' && result.message.trim()
          ? result.message.trim()
          : creditsAdded > 0
            ? t(2611)
            : t(2612);

      const availabilityMessage =
        availableCredits > 0
          ? ` ${t(2613, { values: { credits: availableCredits } })}`
          : '';

      publishFeedback({
        type: 'success',
        message:
          creditsAdded > 0
            ? `${responseMessage}. ${t(2614)}${availabilityMessage}`
            : `${responseMessage}.${availabilityMessage}`,
      });
    },
    onError: (error: unknown) => {
      logger.warn('TasksWidget', 'Failed to claim task reward', error);
      publishFeedback({
        type: 'error',
        message: t(2426),
      });
    },
    onSettled: () => {
      setClaimingTaskKey(null);
    },
  });

  // Task rewards are account-specific business data. A failed refresh must
  // hide cached claim buttons instead of showing them beside an error banner.
  const tasks = tasksQuery.isError ? [] : tasksQuery.data || [];

  if (!tasksQuery.isLoading && !tasksQuery.isError && tasks.length === 0 && hideWhenEmpty) {
    return null;
  }

  return (
    <div className={`${styles.widget}${appearance === 'wheel' ? ` ${styles.wheel}` : ''}`}>
      <h4 className={styles.title}>{title}</h4>
      {tasksQuery.isLoading ? <div className={styles.feedback}>{t(1435)}...</div> : null}
      {tasksQuery.isError ? (
        <div className={styles.feedback} role="alert">
          <div>{t(2425)}</div>
          <button
            type="button"
            disabled={tasksQuery.isFetching}
            onClick={() => { void tasksQuery.refetch(); }}
            className={styles.claimButton}
          >
            {t(2092)}
          </button>
        </div>
      ) : null}
      {!tasksQuery.isLoading && !tasksQuery.isError && tasks.length === 0 && !hideWhenEmpty ? (
        <div className={styles.feedback}>{t(2551)}</div>
      ) : null}
      <div className={styles.accordion}>
        {tasks.map((task: TaskItem) => (
          <details
            key={task.taskKey}
            className={styles.taskItem}
            data-status={task.status}
            data-claimable={task.claimable ? 'true' : 'false'}
            open={openTaskKey === task.taskKey}
            onToggle={(event) => {
              if ((event.currentTarget as HTMLDetailsElement).open) {
                setOpenTaskKey(task.taskKey);
              } else if (openTaskKey === task.taskKey) {
                setOpenTaskKey(null);
              }
            }}
          >
            <summary
              className={styles.taskQuestion}
              onClick={(event) => {
                event.preventDefault();
                setOpenTaskKey((prev) => (prev === task.taskKey ? null : task.taskKey));
              }}
            >
              <div className={styles.taskQuestionCopy}>
                <strong>{task.title}</strong>
                <span className={styles.taskQuestionMeta}>
                  {t(2615, { values: { amount: task.reward.amount } })}
                  {task.reward.expireDays > 0
                    ? ` · ${t(2616, { values: { days: task.reward.expireDays } })}`
                    : ''}
                </span>
              </div>
              <div className={styles.taskQuestionAside}>
                <span className={styles.taskStatus}>{getTaskStatusLabel(task)}</span>
                <span className={styles.taskChevron} aria-hidden="true" />
              </div>
            </summary>
            <div className={styles.taskAnswer}>
              {task.description ? <p className={styles.taskDescription}>{task.description}</p> : null}
              <div className={styles.rewardMeta}>
                {t(2615, { values: { amount: task.reward.amount } })}
                {task.reward.expireDays > 0
                  ? ` · ${t(2616, { values: { days: task.reward.expireDays } })}`
                  : ''}
              </div>
              {task.progressLines?.length ? (
                <div className={styles.progressList}>
                  {task.progressLines.map((line, idx) => {
                    const pct = progressPercent(line);
                    const isComplete = pct >= 100;
                    return (
                      <div
                        key={`${task.taskKey}-${line.type}-${idx}`}
                        className={`${styles.progressLine} ${
                          isComplete ? styles.progressLineComplete : ''
                        }`}
                        style={{ ['--progress' as string]: `${pct}%` }}
                      >
                        {formatProgressLine(line, settings.currency)}
                      </div>
                    );
                  })}
                </div>
              ) : null}
              <div className={styles.taskFooter}>
                <div className={styles.taskStatusDetail}>
                  {t(2617)}: <strong>{getTaskStatusLabel(task)}</strong>
                </div>
                <button
                  type="button"
                  disabled={task.status === 'claimed' || !task.claimable || claimMutation.isPending}
                  onClick={() => claimMutation.mutate(task.taskKey)}
                  className={styles.claimButton}
                >
                  {getTaskButtonLabel(task, claimMutation.isPending && claimingTaskKey === task.taskKey)}
                </button>
              </div>
            </div>
          </details>
        ))}
      </div>
    </div>
  );
};

export default TasksWidget;
